// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

export function AddressesPanel({ user }) {
  // Table is `delivery_addresses`, NOT `user_addresses`. Required columns:
  // user_id, label, recipient_name, street_address, phone. Optional:
  // province, city, town, landmark, instructions, is_default.
  const queryClient = useQueryClient();
  const queryKey = ["addresses", user?.id];
  const [newAddr, setNewAddr] = useState({
    label: "",
    recipient_name: "",
    street_address: "",
    phone: "",
    landmark: "",
    instructions: "",
  });
  const [error, setError] = useState(null);

  const { data: addresses = [], isLoading: loading } = useQuery({
    queryKey,
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from("delivery_addresses")
        .select("*")
        .eq("user_id", user.id)
        .order("is_default", { ascending: false });
      if (err) throw err;
      return data || [];
    },
  });

  async function addAddress() {
    if (!newAddr.street_address.trim() || !newAddr.recipient_name.trim() || !newAddr.phone.trim()) return;
    setError(null);
    const { data, error: err } = await supabase
      .from("delivery_addresses")
      .insert({
        user_id: user.id,
        label: newAddr.label.trim() || "Address",
        recipient_name: newAddr.recipient_name.trim(),
        street_address: newAddr.street_address.trim(),
        phone: newAddr.phone.trim(),
        landmark: newAddr.landmark.trim() || null,
        instructions: newAddr.instructions.trim() || null,
        is_default: addresses.length === 0,
      })
      .select()
      .single();
    if (err) {
      setError(err.message);
      return;
    }
    if (data) {
      queryClient.setQueryData(queryKey, (prev = []) => [data, ...prev]);
      setNewAddr({ label: "", recipient_name: "", street_address: "", phone: "", landmark: "", instructions: "" });
    }
  }

  async function removeAddress(id) {
    await supabase.from("delivery_addresses").delete().eq("id", id);
    queryClient.setQueryData(queryKey, (prev = []) => prev.filter((a) => a.id !== id));
  }

  async function setDefault(id) {
    await supabase.from("delivery_addresses").update({ is_default: false }).eq("user_id", user.id);
    await supabase.from("delivery_addresses").update({ is_default: true }).eq("id", id);
    queryClient.setQueryData(queryKey, (prev = []) => prev.map((a) => ({ ...a, is_default: a.id === id })));
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card pad={22}>
        <Eyebrow>Saved addresses</Eyebrow>
        {loading ? <div style={{ marginTop: 10 }}><Skeleton h={60} /></div> : addresses.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted, padding: "10px 0" }}>No saved addresses yet. Add one below.</div>
        ) : (
          <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
            {addresses.map((a) => (
              <div key={a.id} style={{
                padding: 12, borderRadius: 10,
                border: a.is_default ? `2px solid ${C.hunter}` : "1px solid rgba(196,193,183,0.35)",
                background: a.is_default ? C.cream : C.white,
                display: "flex", alignItems: "flex-start", gap: 12,
              }}>
                <Icon name="map-pin" size={14} color={C.hunter} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>
                    {a.label}
                    {a.is_default && <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px", borderRadius: 6, background: C.hunter, color: C.dust, fontWeight: 800, letterSpacing: 0.5 }}>DEFAULT</span>}
                  </div>
                  {a.recipient_name && <div style={{ fontSize: 12, color: C.slate, marginTop: 2, fontWeight: 700 }}>{a.recipient_name}</div>}
                  <div style={{ fontSize: 12, color: C.slate, marginTop: 2 }}>{a.street_address}</div>
                  {(a.town || a.city || a.province) && (
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                      {[a.town, a.city, a.province].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {a.landmark && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>Near: {a.landmark}</div>}
                  {a.phone && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{a.phone}</div>}
                  {a.instructions && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, fontStyle: "italic" }}>"{a.instructions}"</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {!a.is_default && <Btn size="sm" kind="outline" onClick={() => setDefault(a.id)}>Default</Btn>}
                  <button onClick={() => removeAddress(a.id)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}>
                    <Icon name="trash-2" size={14} color={C.red} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card pad={22}>
        <Eyebrow>Add new address</Eyebrow>
        <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
          <Field label="Label (Home, Work, Hostel, …)"><input value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} style={inputStyle} /></Field>
          <Field label="Recipient name"><input value={newAddr.recipient_name} onChange={(e) => setNewAddr({ ...newAddr, recipient_name: e.target.value })} style={inputStyle} /></Field>
          <Field label="Street address"><textarea value={newAddr.street_address} onChange={(e) => setNewAddr({ ...newAddr, street_address: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} /></Field>
          <Field label="Landmark (optional)"><input value={newAddr.landmark} onChange={(e) => setNewAddr({ ...newAddr, landmark: e.target.value })} style={inputStyle} placeholder="e.g. next to Mr Price" /></Field>
          <Field label="Delivery instructions (optional)"><input value={newAddr.instructions} onChange={(e) => setNewAddr({ ...newAddr, instructions: e.target.value })} style={inputStyle} placeholder="e.g. call when outside" /></Field>
          <Field label="Contact phone"><input value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} style={inputStyle} /></Field>
          <Btn
            onClick={addAddress}
            disabled={!newAddr.street_address.trim() || !newAddr.recipient_name.trim() || !newAddr.phone.trim()}
            icon="plus"
          >
            Add address
          </Btn>
          {error && <span style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>{error}</span>}
        </div>
      </Card>
    </div>
  );
}
